import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import { apiLogin } from "@services/api"; // TEMP DEBUG
import logoIcon from "@assets/icons/logo_icon.png";

const FEATURES = [
  { icon: "fa-bolt",          text: "Instant job matching powered by AI"      },
  { icon: "fa-chart-pie",     text: "Real-time resource utilization dashboard" },
  { icon: "fa-shield-halved", text: "Enterprise-grade security & compliance"   },
];

const validators = {
  email:    v => !v.trim() ? "Email is required" : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? "Enter a valid email address" : "",
  password: v => !v ? "Password is required" : v.length < 6 ? "Password must be at least 6 characters" : "",
};

export default function LoginPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { login, verifyOtp, isAuthenticated, user } = useAuth();

  const [fields,  setFields]  = useState({ email: "", password: "" });
  const [errors,  setErrors]  = useState({ email: "", password: "" });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [debugRaw, setDebugRaw] = useState(null); // TEMP DEBUG
  const [debugComputed, setDebugComputed] = useState(null); // TEMP DEBUG
  const [debugCaughtError, setDebugCaughtError] = useState(null); // TEMP DEBUG
  const [otpStep,    setOtpStep]    = useState(false);
  const [otpUserSlug, setOtpUserSlug] = useState(null);
  const [otpCode,    setOtpCode]    = useState("");
  const [otpError,   setOtpError]   = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  // Resource role lands directly on their own profile, not the generic
  // dashboard (which shows recruiter-oriented widgets irrelevant to them).
  const getDefaultRedirect = (u) =>
    u?.user_primary_role === "3" && (u?.user_slug || u?.slug)
      ? `/profile/${u.user_slug || u.slug}`
      : "/dashboard";

  const explicitFrom = location.state?.from?.pathname || null;
  const from = explicitFrom || getDefaultRedirect(user);
  useEffect(() => { if (isAuthenticated) navigate(from, { replace: true }); }, [isAuthenticated]);

  const setField = (key, val) => {
    setFields(f => ({ ...f, [key]: val }));
    setAuthError("");
    if (touched[key]) setErrors(e => ({ ...e, [key]: validators[key](val) }));
  };

  const blur = (key) => {
    setTouched(t => ({ ...t, [key]: true }));
    setErrors(e => ({ ...e, [key]: validators[key](fields[key]) }));
  };

  const hasError = k => touched[k] && errors[k];
  const isOk     = k => touched[k] && !errors[k] && fields[k];

  const borderColor = k => hasError(k) ? "#DC2626" : isOk(k) ? "#16A34A" : "var(--fc-border, #E5E7EB)";
  const boxShadow   = k => hasError(k) ? "0 0 0 3px rgba(239,68,68,0.1)" : isOk(k) ? "0 0 0 3px rgba(16,185,129,0.1)" : "none";

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { email: validators.email(fields.email), password: validators.password(fields.password) };
    setErrors(newErrors);
    setTouched({ email: true, password: true });
    if (newErrors.email || newErrors.password) return;
    setLoading(true);
    setAuthError("");
    try {
      const rawResponse = await apiLogin(fields.email, fields.password); // TEMP DEBUG
      setDebugRaw(rawResponse); // TEMP DEBUG
      const statusCheck = rawResponse?.status === 1; // TEMP DEBUG
      const otpCheck = !!rawResponse?.requires_otp; // TEMP DEBUG
      const result = statusCheck && otpCheck
        ? { requiresOtp: true, userSlug: rawResponse.user_slug }
        : (statusCheck && rawResponse?.userDetails ? rawResponse.userDetails : null);
      setDebugComputed({ statusCheck, otpCheck, statusType: typeof rawResponse?.status, statusValue: rawResponse?.status, result }); // TEMP DEBUG
      if (!result) throw new Error(rawResponse?.message || "Invalid credentials");
      if (result?.requiresOtp) {
        setOtpUserSlug(result.userSlug);
        setOtpStep(true);
        setOtpCode("");
        setOtpError("");
      } else {
        navigate(explicitFrom || getDefaultRedirect(result), { replace: true });
      }
    } catch (err) {
      setDebugCaughtError(err?.message || String(err)); // TEMP DEBUG
      setAuthError("Invalid email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) return;
    setOtpLoading(true);
    setOtpError("");
    try {
      const loggedInUser = await verifyOtp(otpUserSlug, otpCode);
      navigate(explicitFrom || getDefaultRedirect(loggedInUser), { replace: true });
    } catch (err) {
      setOtpError(err?.message || "Incorrect code. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const InlineMsg = ({ field }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: 12,
      color: hasError(field) ? "#DC2626" : "#16A34A",
      opacity: (hasError(field) || isOk(field)) ? 1 : 0, minHeight: 18, transition: "opacity 0.15s" }}>
      {hasError(field) && <><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} />{errors[field]}</>}
      {isOk(field)     && <><i className="fa-solid fa-circle-check"       style={{ fontSize: 11 }} />Looks good!</>}
    </div>
  );

  return (
    <div className="fc-login-page">

      {/* ── Left Panel ── */}
      <div className="fc-login-left d-none d-lg-flex">
        <div className="fc-login-left-bg">
          <div className="fc-login-left-glow1" />
          <div className="fc-login-left-glow2" />
        </div>

        <Link to="/" className="fc-login-logo-link">
          <img src={logoIcon} alt="FC" style={{ width: 36, height: 36, objectFit: "contain", flexShrink: 0 }} />
          <span className="fc-login-logo-name">
            Firm<span className="fc-login-logo-accent">Connector</span>
          </span>
        </Link>

        <div className="fc-login-left-content">
          <h2 className="fc-login-left-title">Your gateway to smarter resource management</h2>
          <p className="fc-login-left-sub">
            Join thousands of firms, job seekers, and resource managers already using FirmConnector.
          </p>
          {FEATURES.map(f => (
            <div key={f.text} className="fc-login-feature">
              <div className="fc-login-feature-icon"><i className={`fa-solid ${f.icon}`} /></div>
              <span className="fc-login-feature-text">{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right Panel ── */}
      <div className="fc-login-right">
        <div className="fc-login-form-wrap">

          {/* Mobile logo */}
          <div className="d-lg-none text-center mb-4">
            <Link to="/" className="fc-login-logo-link justify-content-center">
              <img src={logoIcon} alt="FC" style={{ width: 30, height: 30, objectFit: "contain" }} />
              <span className="fc-login-logo-name" style={{ color: "var(--fc-text-primary)" }}>
                Firm<span style={{ color: "var(--fc-blue)" }}>Connector</span>
              </span>
            </Link>
          </div>

          {!otpStep ? (<>

          {debugRaw && (
            <div style={{ background:"#FEF2F2", border:"2px solid #DC2626", color:"#111827", fontSize:11, fontFamily:"monospace", padding:"10px 12px", marginBottom:16, borderRadius:8, wordBreak:"break-all", whiteSpace:"pre-wrap" }}>
              RAW BACKEND RESPONSE:{"\n"}{JSON.stringify(debugRaw, null, 2)}
              {"\n\n"}COMPUTED:{"\n"}{JSON.stringify(debugComputed, null, 2)}
              {"\n\n"}CAUGHT ERROR: {debugCaughtError || "(none)"}
            </div>
          )}

          <h1 className="fc-login-heading">Welcome back 👋</h1>
          <p className="fc-login-desc">Sign in to your account to continue</p>

          {/* Auth-level error */}
          {authError && (
            <div className="fc-login-error">
              <i className="fa-solid fa-circle-exclamation" /> {authError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>

            {/* Email */}
            <div className="mb-1">
              <div className="fc-input-icon-wrap">
                <i className="fa-solid fa-envelope fc-input-icon-left"
                  style={{ color: hasError("email") ? "#DC2626" : isOk("email") ? "#16A34A" : undefined, transition: "color 0.18s" }} />
                <input type="email" className="form-control fc-input-padl"
                  placeholder="you@company.com"
                  value={fields.email}
                  onChange={e => setField("email", e.target.value)}
                  onBlur={() => blur("email")}
                  style={{ borderColor: borderColor("email"), boxShadow: boxShadow("email"), transition: "border-color 0.18s, box-shadow 0.18s" }}
                />
                {isOk("email") && <i className="fa-solid fa-circle-check" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#16A34A", fontSize: 14, pointerEvents: "none" }} />}
              </div>
              <InlineMsg field="email" />
            </div>

            {/* Password */}
            <div className="mb-1" style={{ marginTop: 12 }}>
              <div className="fc-input-icon-wrap">
                <i className="fa-solid fa-lock fc-input-icon-left"
                  style={{ color: hasError("password") ? "#DC2626" : isOk("password") ? "#16A34A" : undefined, transition: "color 0.18s" }} />
                <input type={showPass ? "text" : "password"}
                  className="form-control fc-input-padl fc-input-padr"
                  placeholder="Min. 6 characters"
                  value={fields.password}
                  onChange={e => setField("password", e.target.value)}
                  onBlur={() => blur("password")}
                  style={{ borderColor: borderColor("password"), boxShadow: boxShadow("password"), transition: "border-color 0.18s, box-shadow 0.18s" }}
                />
                <button type="button" className="fc-input-icon-right" onClick={() => setShowPass(!showPass)}>
                  <i className={`fa-solid ${showPass ? "fa-eye-slash" : "fa-eye"}`} />
                </button>
              </div>
              <InlineMsg field="password" />
            </div>

            <div className="text-end mb-4" style={{ marginTop: 6 }}>
              <Link to="/forgot-password" className="fc-forgot-link">Forgot password?</Link>
            </div>

            <button type="submit" className="fc-btn fc-btn-md fc-btn-primary fc-btn-full" disabled={loading}
              style={{ fontSize: 15 }}>
              {loading
                ? <><i className="fa-solid fa-spinner fa-spin me-2" />Signing in...</>
                : <><i className="fa-solid fa-right-to-bracket me-2" />Sign In</>}
            </button>
          </form>

          <p className="fc-login-register" style={{ marginTop: 20 }}>
            Don't have an account? <Link to="/contact">Contact sales</Link>
          </p>

          </>) : (<>

          <button type="button" onClick={() => { setOtpStep(false); setOtpCode(""); setOtpError(""); }}
            style={{ background:"none", border:"none", padding:0, marginBottom:16, display:"flex", alignItems:"center", gap:6, color:"var(--fc-text-muted, #6B7280)", fontSize:13, cursor:"pointer" }}>
            <i className="fa-solid fa-arrow-left" /> Back
          </button>

          <h1 className="fc-login-heading">Check your email</h1>
          <p className="fc-login-desc">Enter the 6-digit code we sent you. It expires in 10 minutes.</p>

          {otpError && (
            <div className="fc-login-error">
              <i className="fa-solid fa-circle-exclamation" /> {otpError}
            </div>
          )}

          <form onSubmit={handleOtpSubmit} noValidate>
            <div className="mb-3">
              <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000" autoFocus
                style={{ width:"100%", padding:"12px 16px", fontSize:24, letterSpacing:"0.3em", textAlign:"center", fontFamily:"monospace", border:"1.5px solid var(--fc-border, #E5E7EB)", borderRadius:10, outline:"none" }} />
            </div>

            <button type="submit" className="fc-btn fc-btn-md fc-btn-primary fc-btn-full" disabled={otpLoading || otpCode.length !== 6}
              style={{ fontSize: 15 }}>
              {otpLoading
                ? <><i className="fa-solid fa-spinner fa-spin me-2" />Verifying...</>
                : <><i className="fa-solid fa-shield-halved me-2" />Verify & Sign In</>}
            </button>
          </form>

          <p className="fc-login-register" style={{ marginTop: 20 }}>
            Didn't get a code? <button type="button" onClick={handleSubmit} style={{ background:"none", border:"none", padding:0, color:"var(--fc-blue, #2563EB)", cursor:"pointer", textDecoration:"underline" }}>Resend</button>
          </p>

          </>)}
        </div>
      </div>
    </div>
  );
}